/* eslint-disable no-restricted-imports */
import React, { FC, useEffect, useMemo, useState } from 'react';
import { withCollectionCallbacks } from '../../routing/users/withCollectionCallbacks';
import { useBackButton } from '../../utils/useBackButton';
import { IconArrowLeft } from '../icon/IconArrowLeft';
import { Box } from '../layout/Box';
import { DataTestId } from '../layout/DataTestIdWrapper';
import { getLinkProps } from '../posts/utils/getLinkProps';
import { Toolbar } from '../surface/Toolbar';
import { CollectionMenu } from '../users/CollectionMenu';
import { CollectionAuthorInfo } from './CollectionAuthorInfo/CollectionAuthorInfo';
import { HEADER_BUTTON_WIDTH } from './constants';
import { useHeaderSizes } from './hooks/useHeaderSizes';
import { HeaderContainer, HeaderContent, CenterContent, AnimatedContent } from './styles';
import { CollectionStickyHeaderProps } from './types';

const EnhancedCollectionMenu = withCollectionCallbacks(CollectionMenu);

export const CollectionStickyHeader: FC<CollectionStickyHeaderProps> = ({
    collectionModel,
    showAuthorContent = false,
    collectionInfo,
}) => {
    const { defaultCollection, author } = collectionModel || {};

    const onNavigateBack = useBackButton();

    const [isAnimating, setIsAnimating] = useState(false);
    const [currentContent, setCurrentContent] = useState<'collection' | 'author'>('collection');

    useEffect(() => {
        const targetContent = showAuthorContent ? 'author' : 'collection';

        if (targetContent !== currentContent) {
            setIsAnimating(true);

            // Задержка для смены контента в середине анимации
            const timer = setTimeout(() => {
                setCurrentContent(targetContent);
                setIsAnimating(false);
            }, 150); // Половина времени анимации (300ms / 2)

            return () => clearTimeout(timer);
        }
    }, [showAuthorContent, currentContent]);

    const authorInfo = (
        <CollectionAuthorInfo collectionModel={collectionModel} view="collectionHeader" />
    );

    const { headerHeight, headerButtonTop } = useHeaderSizes();

    const backButton = useMemo(
        () => (
            <Box
                width={HEADER_BUTTON_WIDTH}
                p={1}
                position="absolute"
                left={1}
                top={headerButtonTop}
                dataTestId={DataTestId.COLLECTION_NAVIGATE_BACK}
            >
                <IconArrowLeft
                    cursor="pointer"
                    color="var(--color-primary)"
                    size={24}
                    onClick={onNavigateBack}
                />
            </Box>
        ),
        [headerButtonTop, onNavigateBack],
    );

    const menuItem = useMemo(
        () =>
            collectionModel &&
            !defaultCollection &&
            (!collectionInfo || currentContent === 'author') ? (
                <Box position="absolute" right={1} top={headerButtonTop}>
                    <EnhancedCollectionMenu collection={collectionModel} />
                </Box>
            ) : null,
        [collectionInfo, collectionModel, currentContent, defaultCollection, headerButtonTop],
    );

    if (!collectionModel) {
        return null;
    }

    return (
        <Toolbar
            position="absolute"
            width="100%"
            borderRadius={{ xs: 0, sm: '24px 24px 0 0' }}
            overflow="hidden"
            top={{ xs: 0, sm: '16px' }}
        >
            <HeaderContainer height={headerHeight}>
                <HeaderContent>
                    {backButton}
                    {currentContent === 'author' ? (
                        <CenterContent>
                            <AnimatedContent
                                style={{
                                    opacity: isAnimating ? 0 : 1,
                                }}
                                mx={'auto'}
                                {...getLinkProps(author?.shareLink)}
                            >
                                {authorInfo}
                            </AnimatedContent>
                        </CenterContent>
                    ) : (
                        <CenterContent>
                            <AnimatedContent
                                style={{
                                    opacity: isAnimating ? 0 : 1,
                                }}
                            >
                                {collectionInfo}
                            </AnimatedContent>
                        </CenterContent>
                    )}
                    {menuItem}
                </HeaderContent>
            </HeaderContainer>
        </Toolbar>
    );
};
